import React, { useState, useEffect } from 'react';
import StepperHeader from './StepperHeader';
import { GroupHeader } from './GroupHeader';
import PersonnelManagement from './PersonnelManagement';
import GroupPreview from './GroupPreview';
import { getDepartments } from '../../services/departmentService';

interface GroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: any) => Promise<void>;
  initialData?: {
    title: string;
    departmentId: number | string;
    description?: string;
    personnel: { id: number; name: string; role: string }[];
  };
}

const steps = ['Basic Details', 'Personnel Management', 'Preview'];

const GroupModal = ({ isOpen, onClose, onSubmit, initialData }: GroupModalProps) => {
  const isEditing = Boolean(initialData);
  const [currentStep, setCurrentStep] = useState(0);
  const [groupName, setGroupName] = useState('');
  const [department, setDepartment] = useState('');
  const [description, setDescription] = useState('');

  const [departments, setDepartments] = useState<{ id: number; name: string }[]>([]);

  const [personnel, setPersonnel] = useState<{ id: number; name: string; role: string }[]>([]);

  const [errors, setErrors] = useState({ groupName: '', department: '', personnel: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const data = await getDepartments();
        setDepartments(data);
      } catch (error) {
        console.error('Failed to fetch departments:', error);
      }
    };
    fetchDepartments();
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    setCurrentStep(0);
    setGroupName(initialData?.title ?? '');
    setDepartment(initialData ? String(initialData.departmentId) : '');
    setDescription(initialData?.description ?? '');
    setPersonnel(initialData?.personnel ?? []);
    setErrors({ groupName: '', department: '', personnel: '' });
  }, [initialData, isOpen]);
  useEffect(() => {
    if (personnel.length > 0) {
      setErrors((prev) => ({ ...prev, personnel: '' }));
    }
  }, [personnel]);

  const handleClose = () => {
    if (isSubmitting) return;

    setCurrentStep(0);
    setGroupName('');
    setDepartment('');
    setDescription('');
    setPersonnel([]);
    setErrors({ groupName: '', department: '', personnel: '' });
    onClose();
  };

  const handleBackOrCancel = () => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    } else {
      handleClose();
    }
  };

  if (!isOpen) return null;

  const validateStep0 = () => {
    const newErrors = { groupName: '', department: '', personnel: '' };
    let valid = true;

    if (!groupName.trim()) {
      newErrors.groupName = 'Group name is required';
      valid = false;
    }

    if (!department) {
      newErrors.department = 'Department is required';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const validateStep1 = () => {
    if (personnel.length === 0) {
      setErrors((prev) => ({ ...prev, personnel: 'Please add at least one personnel.' }));
      return false;
    }
    setErrors((prev) => ({ ...prev, personnel: '' }));
    return true;
  };

  const handleSubmit = async () => {
    const payload = {
      title: groupName,
      department_id: Number(department), // replace with actual selected department id
      description,
      users: personnel.map((person) => ({
        user_id: person.id,
      })),
    };

    try {
      setIsSubmitting(true);

      await onSubmit(payload);

      handleClose();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={handleClose}>
      <div className="bg-white rounded-xl shadow-xl w-[560px]" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <GroupHeader onClose={handleClose} title={isEditing ? 'EDIT Group' : 'ADD Group'} />
        {/* Stepper */}
        <StepperHeader steps={steps} activeStep={currentStep} />

        {/* Body */}
        <div className="px-6 py-5">
          {currentStep === 0 && (
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Group Name"
                    value={groupName}
                    onChange={(e) => {
                      setGroupName(e.target.value);
                      if (e.target.value.trim()) setErrors((prev) => ({ ...prev, groupName: '' }));
                    }}
                    className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none ${
                      errors.groupName ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-red-400'
                    }`}
                  />
                  {errors.groupName && <p className="text-red-500 text-xs mt-1">{errors.groupName}</p>}
                </div>

                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">
                    Department <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={department}
                    onChange={(e) => {
                      setDepartment(e.target.value);
                      if (e.target.value) setErrors((prev) => ({ ...prev, department: '' }));
                    }}
                    className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none bg-white ${
                      errors.department ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-red-400'
                    }`}
                  >
                    <option value="">Select Department</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                  {errors.department && <p className="text-red-500 text-xs mt-1">{errors.department}</p>}
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">Description</label>

                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full resize-none border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400"
                  rows={3}
                />
              </div>
            </div>
          )}

          {currentStep === 1 && <PersonnelManagement personnel={personnel} setPersonnel={setPersonnel} personnelError={errors.personnel} />}

          {currentStep === 2 && (
            <GroupPreview
              groupName={groupName}
              department={departments.find((d) => String(d.id) === String(department))?.name || department}
              personnel={personnel}
            />
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100" />

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4">
          <button
            onClick={handleBackOrCancel}
            disabled={isSubmitting}
            className="px-4 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {currentStep > 0 ? 'Back' : 'Cancel'}
          </button>

          {currentStep < steps.length - 1 ? (
            <button
              onClick={() => {
                if (currentStep === 0 && !validateStep0()) return;
                if (currentStep === 1 && !validateStep1()) return;
                setCurrentStep((s) => s + 1);
              }}
              className="px-5 py-1.5 text-sm bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`min-w-[125px] px-5 py-1.5 text-sm rounded-lg font-medium text-white flex items-center justify-center gap-2 ${
                isSubmitting ? 'bg-red-300 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'
              }`}
            >
              {isSubmitting && (
                <svg className="h-4 w-4 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              {isSubmitting ? (isEditing ? 'Updating...' : 'Adding...') : isEditing ? 'Update Group' : 'Add Group'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupModal;
